import React from 'react'

import GlobalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { handleFetchErrors } from './helper'
import {
    ApprovalSettings,
    ApprovalSettingsList,
    ApprovalSettingsType,
    DeletedApprovalSettings,
    defaultApprovalSettingsList,
    newApprovalSettings
} from '../types/ApprovalSettings'
import { Notification } from '../components/Notification'

const requestCommon = {
    credentials: 'same-origin',
    cache: 'no-cache'
} as RequestInit

const jsonHeaders = {
    'Content-Type': 'application/json'
} as HeadersInit

export enum METHODS {
    GET = 'GET',
    POST = 'POST',
    DELETE = 'DELETE',
    PUT = 'PUT'
}
const PREFIX_URL_REPORT = '/services/plugin-reports-approval/api/'
const PREFIX_URL_LIVE = '/rest/'

// FETCHERS
async function fetchApi<T>(url: string, method = METHODS.GET, body?: string | undefined): Promise<T> {
    return await fetch(PREFIX_URL_REPORT + url, { ...requestCommon, headers: jsonHeaders, method, body }).then(
        async response => {
            // reading the stream only once.
            const finalRes = await response.json()

            if (!response.ok) {
                if (finalRes.then) {
                    finalRes.then(errorText => {
                        throw errorText
                    })
                } else {
                    throw new Error(finalRes.error)
                }
            }

            return finalRes
        }
    )
}

export async function getLiveApi<T>(url: string): Promise<T> {
    return await fetch(PREFIX_URL_LIVE + url, { ...requestCommon, headers: jsonHeaders, method: METHODS.GET }).then(
        async response => {
            // reading the stream only one time..
            const finalRes = await response.json()

            if (!response.ok) {
                finalRes.then(errorText => {
                    throw errorText
                })
            }

            return finalRes
        }
    )
}

// GET APPROVAL SETTINGS LIST
export async function getApprovalSettingsList(): Promise<ApprovalSettingsList> {
    try {
        const res = await fetchApi<ApprovalSettingsList>('approvalSettingsList')
        if (!res.success) {
            GlobalNotification.error(
                <Notification title={i18n('Error trying to get the approval settings list: ')}>
                    {'unknown error'}
                </Notification>,
                20
            )

            console.error(res)
        }

        return res
    } catch (error) {
        GlobalNotification.error(
            <Notification title={i18n('Error trying to get the approval settings list: ')}>{error}</Notification>,
            20
        )

        console.error(error)

        return defaultApprovalSettingsList
    }
}

// GET SINGLE APPROVAL SETTINGS
export async function getApprovalSettings(scheduleUid: string): Promise<ApprovalSettingsType> {
    try {
        const res = await fetchApi<ApprovalSettings>(`approvalSettings/${scheduleUid}`)
        if (!res.success) {
            GlobalNotification.error(
                <Notification title={i18n('Error trying to get the approval settings: ')}>
                    {'unknown error'}
                </Notification>,
                20
            )

            console.error(res)
        }

        return res.configs
    } catch (error) {
        GlobalNotification.error(
            <Notification title={i18n('Error trying to get the approval settings list: ')}>{error}</Notification>,
            20
        )

        console.error(error)

        return defaultApprovalSettingsList
    }
}

// CREATE OR UPDATE
export async function updateApprovalSettings(approvalSettings: ApprovalSettingsType): Promise<newApprovalSettings> {
    try {
        const res = await fetchApi<newApprovalSettings>(
            `updateApprovalSettings`,
            METHODS.POST,
            JSON.stringify(approvalSettings)
        )
        if (!res.success) {
            GlobalNotification.error(
                <Notification title={i18n('Error trying to update approval settings: ')}>
                    {'unknown error'}
                </Notification>,
                20
            )

            console.error(res)
        }

        return res
    } catch (error) {
        GlobalNotification.error(
            <Notification title={i18n('Error trying to update approval settings:')}>{error}</Notification>,
            20
        )

        console.error(error)

        return null
    }
}

// DELETE
export async function deleteApprovalSettings(uid: string): Promise<DeletedApprovalSettings> {
    try {
        const res = await fetchApi<DeletedApprovalSettings>(`deleteApprovalSettings/${uid}`, METHODS.DELETE)

        return res
    } catch (error) {
        console.error(error)

        return null
    }
}

export async function getServerLocalTimeZone(): Promise<any> {
    try {
        const res = await fetchApi<any>(`approvalSettings/serverLocalTimeZone`)

        return res.timeZone
    } catch (error) {
        GlobalNotification.error(
            <Notification title={i18n('Error trying to get the server local time. Error: ')}>{error}</Notification>,
            20
        )

        return console.error(error)
    }
}

// LIVE APIs
export async function getAlarmById(id: number): Promise<RulesResponse> {
    try {
        const alarmResponse = await getLiveApi<RulesResponse>(`rule/${id}`)

        return alarmResponse
    } catch (error) {
        GlobalNotification.error(
            <Notification title={i18n('Error trying to get Report: ')}>
                <tr>
                    <strong>Alarm id: </strong> {id}
                </tr>
                {error}
            </Notification>,
            20
        )

        console.error(error)

        return {
            data: []
        }
    }
}

export function getLiveRigs(): Promise<any> {
    return fetch(`/services/plugin-liverig/assets/rig`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r.json().then(data => ({ status: r.status, body: data })))
        .then(res => {
            return res.body
        })
        .catch(err => {
            console.error(err)
            GlobalNotification.error(i18n(`Unable to fetch assets for rig.`))
            return []
        })
}

export function getLiveWells(): Promise<{ id: string; name: string }[]> {
    return fetch('/services/plugin-liverig/assets/well', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
    })
        .then(handleFetchErrors)
        .then(r => r?.json && r.json().then(data => ({ status: r?.status, body: data })))
        .then(res => {
            return res?.body ?? []
        })
        .catch(err => {
            console.error(err)
            GlobalNotification.error(i18n(`Unable to fetch live wells.`))
            return []
        })
}
